/**
 * The loader — `incluno.js`.
 *
 * This is the file the customer pastes into their site, so it is the one file
 * that must never need to change. It does the least possible: work out which
 * domain it is on, work out where to fetch from, and pull in the versioned
 * core.
 *
 * Everything else lives in the core bundle, which can be rebuilt and
 * cache-busted per release without touching a single customer's HTML.
 */

interface LoaderConfig {
  apiUrl: string;
  cdnUrl: string;
  domain: string;
}

/**
 * Reads configuration off the script tag.
 *
 * `document.currentScript` is the reliable way to find our own tag while it is
 * executing; the querySelector is the fallback for the case where a tag
 * manager injects the script and currentScript is null.
 */
function readConfig(): LoaderConfig | null {
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-incluno-domain]');

  if (!script) return null;

  const src = script.getAttribute('src') ?? '';
  let cdnUrl = '';
  let apiUrl = script.getAttribute('data-incluno-api') ?? '';

  try {
    const url = new URL(src, window.location.href);
    // The CDN origin is wherever this script was served from, so a customer on
    // a self-hosted copy does not need to configure anything extra.
    cdnUrl = url.origin + url.pathname.replace(/\/[^/]*$/, '');
  } catch {
    return null;
  }

  // Bare identifier, not a quoted string: Vite's `define` substitutes
  // identifiers at build time and leaves string contents alone, so quoting it
  // shipped the placeholder verbatim and every beacon posted to
  // `/__INCLUNO_API_URL__/v1/widget/beacon` on the customer's own origin.
  if (!apiUrl) apiUrl = __INCLUNO_API_URL__;

  // Normalised the same way the server does, so `www.` and casing cannot cause
  // a lookup miss.
  const domain = (script.getAttribute('data-incluno-domain') || window.location.hostname)
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');

  return { apiUrl, cdnUrl, domain };
}

function start(): void {
  const config = readConfig();
  if (!config) return;

  // Never load twice — a customer with the snippet in both a template and a
  // tag manager should still get one widget.
  if (window.__inclunoLoaded) return;
  window.__inclunoLoaded = true;

  const core = document.createElement('script');
  core.src = `${config.cdnUrl}/incluno.core.js`;
  core.async = true;
  core.crossOrigin = 'anonymous';

  core.onload = () => {
    window.Incluno?.boot({ apiUrl: config.apiUrl, domain: config.domain });
  };

  // A failed core load must be silent: the customer's page is not ours to log
  // errors into.
  core.onerror = () => {
    window.__inclunoLoaded = false;
  };

  document.head.appendChild(core);
}

declare global {
  interface Window {
    __inclunoLoaded?: boolean;
    Incluno?: { boot: (options: { apiUrl: string; domain: string }) => void };
  }

  /** Replaced at build time by Vite's `define`. */
  const __INCLUNO_API_URL__: string;
}

start();

export {};
